import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LanguageContext";
import { TAGLINES } from "@/i18n/translations";
import { LanguageSelector } from "@/components/LanguageSelector";
import { BrandLogo } from "@/components/BrandLogo";
import { AppFooter } from "@/components/AppFooter";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Scales, Vault, Stack, FunnelSimple, ArrowRight,
  CheckCircle, Bank, Newspaper, Gavel, Sparkle, Lock, Fingerprint,
} from "@phosphor-icons/react";

const NAVY = "#1B3A5B";
const DARK = "#0a1622";
const ORANGE = "#E07B2E";

const HERO_IMG = "https://images.pexels.com/photos/17485657/pexels-photo-17485657.png";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: "easeOut" } }),
};

function Reveal({ children, i = 0, className = "" }) {
  return (
    <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="show"
      viewport={{ once: true, margin: "-60px" }} className={className}>
      {children}
    </motion.div>
  );
}

function Eyebrow({ children, dark }) {
  return (
    <span className={`text-xs font-mono uppercase tracking-[0.2em] ${dark ? "text-[#7FA8D4]" : "text-[#2E6FB7]"}`}>
      {children}
    </span>
  );
}

export default function Home() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = () => navigate(user ? "/app" : "/register");

  const pillars = [
    { icon: Scales, k: "gate", span: "lg:col-span-7", accent: true },
    { icon: Vault, k: "ledger", span: "lg:col-span-5" },
    { icon: Stack, k: "sources", span: "lg:col-span-5" },
    { icon: FunnelSimple, k: "filter", span: "lg:col-span-7" },
  ];
  const steps = ["step1", "step2", "step3", "step4"];
  const registers = ["InfoCamere", "Registro Imprese", "Gazzetta Ufficiale", "Tribunali", "Camere di Commercio"];
  const tiers = [
    { k: "watcher", featured: false },
    { k: "investigator", featured: true },
    { k: "payper", featured: false },
  ];
  const registerItems = [...registers, ...registers];

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#1B3A5B] text-left" data-testid="home-page">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/")} data-testid="home-logo" className="flex items-center">
            <BrandLogo variant="dark" showTagline />
          </button>
          <div className="ml-auto flex items-center gap-2">
            <LanguageSelector />
            {user ? (
              <Button data-testid="home-open-platform" onClick={() => navigate("/app")}
                className="rounded-md gap-2 text-white hover:opacity-90" style={{ backgroundColor: ORANGE }}>
                {t("nav.openPlatform")} <ArrowRight size={15} weight="bold" />
              </Button>
            ) : (
              <>
                <Button variant="ghost" data-testid="home-login" onClick={() => navigate("/login")}
                  className="hidden sm:inline-flex text-[#1B3A5B] hover:bg-slate-100 rounded-md">
                  {t("nav.login")}
                </Button>
                <Button data-testid="home-register" onClick={() => navigate("/register")}
                  className="rounded-md text-white hover:opacity-90" style={{ backgroundColor: ORANGE }}>
                  {t("nav.register")}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ backgroundColor: DARK }}>
        <div className="absolute inset-0 opacity-[0.35]"
          style={{ background: `radial-gradient(900px 500px at 80% -10%, ${NAVY}, transparent 60%)` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow dark>{TAGLINES[lang] || TAGLINES.it}</Eyebrow></Reveal>
            <Reveal i={1}>
              <div className="mt-4 inline-flex items-center gap-2 border border-white/15 bg-white/5 text-slate-200 text-[11px] font-mono uppercase tracking-wider px-3 py-1 rounded-full">
                <ShieldCheck size={14} weight="fill" style={{ color: ORANGE }} /> {t("hero.badge")}
              </div>
            </Reveal>
            <Reveal i={2}>
              <h1 className="mt-6 font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] text-white">
                {t("hero.title")}
              </h1>
            </Reveal>
            <Reveal i={3}>
              <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl">{t("hero.subtitle")}</p>
            </Reveal>
            <Reveal i={4}>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Button data-testid="hero-cta-primary" onClick={go}
                  className="h-12 px-7 rounded-md text-white hover:opacity-90 gap-2 text-base" style={{ backgroundColor: ORANGE }}>
                  {user ? t("nav.openPlatform") : t("hero.ctaPrimary")} <ArrowRight size={17} weight="bold" />
                </Button>
                {!user && (
                  <Button data-testid="hero-cta-secondary" onClick={() => navigate("/login")} variant="outline"
                    className="h-12 px-7 rounded-md bg-transparent border-white/25 text-white hover:bg-white/10 text-base">
                    {t("hero.ctaSecondary")}
                  </Button>
                )}
              </div>
            </Reveal>
            <Reveal i={5}>
              <div className="mt-8 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">{t("hero.note")}</div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal i={3}>
              <div className="relative rounded-xl border border-white/10 overflow-hidden"
                style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.45)" }}>
                <img src={HERO_IMG} alt="" className="w-full h-[340px] md:h-[420px] object-cover" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 40%, ${DARK})` }} />
                {/* floating chips */}
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/95 backdrop-blur px-3 py-1.5 rounded-md border border-white/60 shadow-lg">
                  <Fingerprint size={16} weight="duotone" style={{ color: NAVY }} />
                  <span className="text-xs font-mono font-semibold" style={{ color: NAVY }}>SHA-256 CHAIN</span>
                </div>
                <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-white/95 backdrop-blur px-3 py-1.5 rounded-md border border-white/60 shadow-lg">
                  <Lock size={16} weight="duotone" style={{ color: ORANGE }} />
                  <span className="text-xs font-mono font-semibold" style={{ color: NAVY }}>ART. 6.1.f GDPR</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* registers marquee */}
        <div className="relative z-10 border-t border-white/10 py-6 avz-marquee-mask">
          <div className="avz-marquee-track gap-12 px-6">
            {registerItems.map((r, i) => (
              <span key={i} className="flex items-center gap-3 text-slate-400 whitespace-nowrap">
                <Bank size={16} weight="duotone" style={{ color: ORANGE }} />
                <span className="font-mono text-sm uppercase tracking-widest">{r}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars — bento */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <Reveal>
          <Eyebrow>Compliance Engine</Eyebrow>
          <h2 className="mt-3 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">{t("pillars.title")}</h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">{t("pillars.subtitle")}</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {pillars.map((p, i) => (
            <Reveal key={p.k} i={i} className={p.span}>
              <div data-testid={`pillar-${p.k}`}
                className="group h-full bg-white border border-slate-200 rounded-xl p-7 transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(27,58,91,0.08)] hover:border-slate-300">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: p.accent ? ORANGE : NAVY }}>
                  <p.icon size={24} weight="duotone" className="text-white" />
                </div>
                <h3 className="mt-5 font-heading text-xl md:text-2xl font-semibold tracking-tight">{t(`pillars.${p.k}.title`)}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed max-w-xl">{t(`pillars.${p.k}.desc`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <Reveal>
            <Eyebrow>Workflow</Eyebrow>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">{t("how.title")}</h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">{t("how.subtitle")}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden">
            {steps.map((s, i) => (
              <Reveal key={s} i={i}>
                <div data-testid={`how-${s}`} className="h-full bg-white p-7 relative">
                  <span className="font-mono text-2xl font-bold tracking-tight" style={{ color: i === 3 ? ORANGE : "#CBD5E1" }}>
                    0{i + 1}
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold tracking-tight">{t(`how.${s}.title`)}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(`how.${s}.desc`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sources */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <Reveal>
          <Eyebrow>Fonti Ufficiali</Eyebrow>
          <h2 className="mt-3 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">{t("sources.title")}</h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">{t("sources.subtitle")}</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[{ icon: Bank, k: "infocamere" }, { icon: Newspaper, k: "gazzetta" }, { icon: Gavel, k: "tribunali" }].map((r, i) => (
            <Reveal key={r.k} i={i}>
              <div data-testid={`source-${r.k}`} className="h-full bg-white border border-slate-200 rounded-xl p-7">
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center border border-slate-200 bg-slate-50">
                    <r.icon size={22} weight="duotone" style={{ color: NAVY }} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white px-2 py-1 rounded-sm" style={{ backgroundColor: NAVY }}>
                    {["P1", "P2", "P3"][i]}
                  </span>
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold tracking-tight">{t(`sources.${r.k}.name`)}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(`sources.${r.k}.desc`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <Reveal className="text-center">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">{t("pricing.title")}</h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">{t("pricing.subtitle")}</p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            {tiers.map((tier, i) => {
              const features = t(`pricing.${tier.k}.features`);
              const feats = Array.isArray(features) ? features : [];
              return (
                <Reveal key={tier.k} i={i}>
                  <div
                    data-testid={`pricing-${tier.k}`}
                    className={`h-full rounded-xl p-7 flex flex-col ${tier.featured ? "lg:-translate-y-4" : ""}`}
                    style={
                      tier.featured
                        ? { backgroundColor: NAVY, color: "#fff", border: `1.5px solid ${ORANGE}`, boxShadow: "0 0 40px rgba(224,123,46,0.22)" }
                        : { backgroundColor: "#fff", border: "1px solid #E2E8F0" }
                    }
                  >
                    {tier.featured && (
                      <span className="self-start inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm mb-3 text-white" style={{ backgroundColor: ORANGE }}>
                        <Sparkle size={11} weight="fill" /> {t("pricing.popular")}
                      </span>
                    )}
                    <h3 className="font-heading text-2xl font-bold tracking-tight">{t(`pricing.${tier.k}.name`)}</h3>
                    <div className={`mt-1 text-sm font-mono ${tier.featured ? "text-slate-300" : "text-slate-500"}`}>{t(`pricing.${tier.k}.price`)}</div>
                    <p className={`mt-4 text-sm ${tier.featured ? "text-slate-200" : "text-slate-600"}`}>{t(`pricing.${tier.k}.tagline`)}</p>
                    <ul className="mt-6 space-y-3 flex-1">
                      {feats.map((f, fi) => (
                        <li key={fi} className="flex items-start gap-2.5 text-sm">
                          <CheckCircle size={18} weight="fill" style={{ color: ORANGE }} className="shrink-0 mt-0.5" />
                          <span className={tier.featured ? "text-slate-100" : "text-slate-700"}>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button data-testid={`pricing-cta-${tier.k}`} onClick={go}
                      className="mt-7 rounded-md text-white hover:opacity-90"
                      style={{ backgroundColor: tier.featured ? ORANGE : NAVY }}>
                      {t("pricing.cta")}
                    </Button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ backgroundColor: NAVY }} className="text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-28 text-center">
          <Reveal>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter max-w-3xl mx-auto leading-[1.05]">{t("finalCta.title")}</h2>
            <p className="mt-5 text-base md:text-lg text-slate-300 max-w-xl mx-auto">{t("finalCta.subtitle")}</p>
            <Button data-testid="final-cta" onClick={go}
              className="mt-9 h-12 px-8 rounded-md text-white hover:opacity-90 gap-2 text-base" style={{ backgroundColor: ORANGE }}>
              {user ? t("nav.openPlatform") : t("finalCta.button")} <ArrowRight size={17} weight="bold" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: DARK }} className="text-slate-400">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <BrandLogo variant="light" showTagline />
            <div className="text-xs md:ml-4 max-w-md">
              <div className="text-slate-300">{t("footer.compliance")}</div>
              <div className="text-slate-500 mt-1 font-mono">{t("footer.mock")}</div>
            </div>
            <div className="md:ml-auto">
              <LanguageSelector variant="dark" />
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center gap-4">
            <AppFooter variant="dark" className="border-0 pt-0" />
            <div className="md:ml-auto text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
              © {new Date().getFullYear()} Certy Trace · {t("footer.rights")}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
